import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Alert } from 'react-bootstrap';
import { FaCopy, FaExpand, FaCompress } from 'react-icons/fa';

/**
 * Recursively searches for agentOutput fields in a nested object structure
 * Prioritizes agentOuput (typo) over agentOutput (correct spelling)
 * Enhanced to handle Step Functions response patterns
 */
const extractAgentOutput = (obj, visited = new Set()) => {
  if (!obj || typeof obj !== 'object' || visited.has(obj)) {
    return null;
  }

  visited.add(obj);

  // Priority 1: Look for agentOuput (with typo)
  if (obj.hasOwnProperty('agentOuput') && obj.agentOuput) {
    return obj.agentOuput;
  }

  // Priority 2: Look for agentOutput (correct spelling)
  if (obj.hasOwnProperty('agentOutput') && obj.agentOutput) {
    return obj.agentOutput;
  }

  // Enhanced Step Functions response handling
  // Check for common Step Functions response patterns
  
  // Pattern 1: result.body (string or object)
  if (obj.body) {
    let bodyData = obj.body;
    
    // If body is a string, try to parse it as JSON
    if (typeof bodyData === 'string') {
      try {
        bodyData = JSON.parse(bodyData);
      } catch (e) {
        // If parsing fails, continue with string value
      }
    }
    
    if (bodyData && typeof bodyData === 'object') {
      const bodyResult = extractAgentOutput(bodyData, visited);
      if (bodyResult) return bodyResult;
    }
  }

  // Pattern 2: result.response.body (common in Step Functions)
  if (obj.response && obj.response.body) {
    let responseBodyData = obj.response.body;
    
    // If response.body is a string, try to parse it as JSON
    if (typeof responseBodyData === 'string') {
      try {
        responseBodyData = JSON.parse(responseBodyData);
      } catch (e) {
        // If parsing fails, continue with string value
      }
    }
    
    if (responseBodyData && typeof responseBodyData === 'object') {
      const responseBodyResult = extractAgentOutput(responseBodyData, visited);
      if (responseBodyResult) return responseBodyResult;
    }
  }

  // Pattern 3: Lambda step outputs and execution results
  if (obj.lambdaSteps && Array.isArray(obj.lambdaSteps)) {
    for (const step of obj.lambdaSteps) {
      if (step.output) {
        let stepOutput = step.output;
        
        // If output is a string, try to parse it as JSON
        if (typeof stepOutput === 'string') {
          try {
            stepOutput = JSON.parse(stepOutput);
          } catch (e) {
            // Continue with string value
          }
        }
        
        if (stepOutput && typeof stepOutput === 'object') {
          const stepResult = extractAgentOutput(stepOutput, visited);
          if (stepResult) return stepResult;
        }
      }
      
      // Also check step details
      if (step.details) {
        const detailsResult = extractAgentOutput(step.details, visited);
        if (detailsResult) return detailsResult;
      }
    }
  }

  // Pattern 4: Execution details (common in Step Functions logs)
  if (obj.executionDetails) {
    const executionResult = extractAgentOutput(obj.executionDetails, visited);
    if (executionResult) return executionResult;
  }

  // Pattern 5: Task results and step function outputs
  if (obj.taskSucceededEventDetails) {
    let taskOutput = obj.taskSucceededEventDetails.output;
    
    if (typeof taskOutput === 'string') {
      try {
        taskOutput = JSON.parse(taskOutput);
      } catch (e) {
        // Continue with string value
      }
    }
    
    if (taskOutput && typeof taskOutput === 'object') {
      const taskResult = extractAgentOutput(taskOutput, visited);
      if (taskResult) return taskResult;
    }
  }

  // Pattern 6: Raw history events (from Step Functions execution history)
  if (obj.rawHistory && Array.isArray(obj.rawHistory)) {
    for (const event of obj.rawHistory) {
      // Check task succeeded events
      if (event.taskSucceededEventDetails && event.taskSucceededEventDetails.output) {
        let eventOutput = event.taskSucceededEventDetails.output;
        
        if (typeof eventOutput === 'string') {
          try {
            eventOutput = JSON.parse(eventOutput);
          } catch (e) {
            // Continue with string value
          }
        }
        
        if (eventOutput && typeof eventOutput === 'object') {
          const eventResult = extractAgentOutput(eventOutput, visited);
          if (eventResult) return eventResult;
        }
      }
      
      // Check other event details
      const eventResult = extractAgentOutput(event, visited);
      if (eventResult) return eventResult;
    }
  }

  // Pattern 7: Direct result field (common in API responses)
  if (obj.result) {
    let resultData = obj.result;
    
    if (typeof resultData === 'string') {
      try {
        resultData = JSON.parse(resultData);
      } catch (e) {
        // Continue with string value
      }
    }
    
    if (resultData && typeof resultData === 'object') {
      const directResult = extractAgentOutput(resultData, visited);
      if (directResult) return directResult;
    }
  }

  // Recursively search nested objects and arrays (original logic)
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      
      if (Array.isArray(value)) {
        // Search through array elements
        for (const item of value) {
          const result = extractAgentOutput(item, visited);
          if (result) return result;
        }
      } else if (typeof value === 'object' && value !== null) {
        // Search nested objects
        const result = extractAgentOutput(value, visited);
        if (result) return result;
      }
    }
  }

  return null;
};

const OverallOutputPanel = ({ executionResult, onPanelViewed }) => {
  const [expanded, setExpanded] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const [hasViewedContent, setHasViewedContent] = useState(false);
  const contentRef = useRef(null);

  // Extract agent output with graceful fallback for nested structures
  const agentOutput = extractAgentOutput(executionResult);
  const hasContent = Boolean(agentOutput);

  // Call onPanelViewed when content is first available
  useEffect(() => {
    if (hasContent && !hasViewedContent && onPanelViewed) {
      onPanelViewed();
      setHasViewedContent(true);
    }
  }, [hasContent, hasViewedContent, onPanelViewed]);

  const handleCopy = async () => {
    if (!agentOutput) return;

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(agentOutput);
        setCopyStatus('success');
      } else {
        // Fallback to document.execCommand for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = agentOutput;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        if (document.execCommand('copy')) {
          setCopyStatus('success');
        } else {
          throw new Error('Copy command failed');
        }
        
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Copy failed:', err);
      setCopyStatus('error');
    }

    // Clear copy status after 3 seconds
    setTimeout(() => setCopyStatus(''), 3000);
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <div 
      className={`overall-output-panel ${expanded ? 'expanded' : ''}`}
      data-testid="overall-output-panel"
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, backgroundColor: 'white' } : {}}
    >
      <div className="overall-output-section-header" style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
        <h3 className="section-title" style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#212529' }} aria-label="Overall Output">
          Overall Output
        </h3>
        <div className="panel-actions" style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleCopy}
            disabled={!hasContent}
            aria-label="Copy overall output to clipboard"
            title={hasContent ? "Copy overall output to clipboard" : "No content to copy"}
          >
            <FaCopy className="me-1" />
            {copyStatus === 'success' ? 'Copied!' : 'Copy'}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={toggleExpanded}
            aria-label={expanded ? "Collapse panel" : "Expand panel"}
          >
            {expanded ? <FaCompress /> : <FaExpand />}
          </Button>
        </div>
      </div>

      <div 
        className="overall-output-content" 
        style={{ 
          flex: 1, 
          padding: '1rem', 
          overflow: 'auto',
          minHeight: expanded ? 'calc(100vh - 120px)' : '400px',
          maxHeight: expanded ? 'calc(100vh - 120px)' : '400px'
        }}
        role="region"
        aria-label="Overall output content"
        aria-live="polite"
        ref={contentRef}
      >
        {hasContent ? (
          <div className="output-display">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#212529',
                backgroundColor: '#f8f9fa',
                padding: '1rem',
                borderRadius: '4px',
                border: '1px solid #dee2e6',
                margin: 0,
                overflow: 'auto'
              }}
            >
              {typeof agentOutput === 'object' ? JSON.stringify(agentOutput, null, 2) : agentOutput}
            </pre>
          </div>
        ) : (
          <div className="no-output-message" style={{ textAlign: 'center', color: '#6c757d', padding: '2rem 0' }}>
            <p>No overall output available</p>
            <small>Overall output will appear here once the execution completes.</small>
          </div>
        )}
      </div>

      {/* Copy status feedback */}
      {copyStatus && (
        <div 
          role="status" 
          aria-live="polite" 
          style={{ position: 'absolute', bottom: '1rem', right: '1rem' }}
        >
          <Alert 
            variant={copyStatus === 'success' ? 'success' : 'danger'} 
            style={{ 
              margin: 0, 
              padding: '0.5rem 0.75rem',
              fontSize: '0.875rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          >
            {copyStatus === 'success' 
              ? 'Content copied to clipboard successfully!' 
              : 'Failed to copy content to clipboard'
            }
          </Alert>
        </div>
      )}

      {/* Accessibility announcement for copy status */}
      <div 
        role="status" 
        aria-live="polite" 
        className="sr-only"
        style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}
      >
        {copyStatus === 'success' && 'Content copied to clipboard successfully'}
        {copyStatus === 'error' && 'Failed to copy content'}
      </div>
    </div>
  );
};

export default OverallOutputPanel;